'use client'

import Script from 'next/script'
import { useEffect, useRef } from 'react'
import { useNavigationStore } from '@/store/use-navigation-store'

// Environment variables for analytics IDs
const YANDEX_METRIKA_ID = process.env.NEXT_PUBLIC_YANDEX_METRIKA_ID
const GA_ID = process.env.NEXT_PUBLIC_GA_ID

/**
 * usePageView — tracks SPA page changes via useNavigationStore.
 * Sends pageview events to Yandex.Metrica and Google Analytics.
 */
function usePageView() {
  const page = useNavigationStore((s) => s.page)
  const params = useNavigationStore((s) => s.params)
  const isFirstRender = useRef(true)

  useEffect(() => {
    // Skip the first render — the initial pageview is handled by script initialization
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const pagePath =
      page === 'home'
        ? '/'
        : page === 'product'
          ? `/product/${params.id || ''}`
          : page === 'category'
            ? `/category/${params.id || ''}`
            : `/${page}`

    // Yandex.Metrica pageview
    if (YANDEX_METRIKA_ID && typeof window !== 'undefined') {
      try {
        const ym = (window as any).ym
        if (ym) {
          ym(YANDEX_METRIKA_ID, 'hit', pagePath)
        }
      } catch {
        // Silently fail if Yandex.Metrica is not available
      }
    }

    // Google Analytics pageview
    if (GA_ID && typeof window !== 'undefined') {
      try {
        const gtag = (window as any).gtag
        if (gtag) {
          gtag('config', GA_ID, { page_path: pagePath })
        }
      } catch {
        // Silently fail if GA is not available
      }
    }
  }, [page, params])
}

/**
 * Ecommerce event helpers — exposed globally on window for use from the hook.
 */
function initEcommerceHelpers() {
  if (typeof window === 'undefined') return

  const win = window as any

  if (!win.__ecommerceHelpers) {
    win.__ecommerceHelpers = {
      /**
       * Track add-to-cart ecommerce event
       */
      trackAddToCart(product: {
        id: string
        name: string
        price: number
        brand?: string
        category?: string
        variant?: string
        quantity?: number
      }) {
        try {
          // Yandex.Metrica ecommerce
          if (YANDEX_METRIKA_ID && win.ym) {
            win.ym(YANDEX_METRIKA_ID, 'reachGoal', 'add_to_cart', {
              ecommerce: {
                add: {
                  products: [
                    {
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      brand: product.brand,
                      category: product.category,
                      variant: product.variant,
                      quantity: product.quantity || 1,
                    },
                  ],
                },
              },
            })
          }

          // GA4 ecommerce
          if (GA_ID && win.gtag) {
            win.gtag('event', 'add_to_cart', {
              currency: 'RUB',
              value: product.price * (product.quantity || 1),
              items: [
                {
                  item_id: product.id,
                  item_name: product.name,
                  item_brand: product.brand,
                  item_category: product.category,
                  item_variant: product.variant,
                  price: product.price,
                  quantity: product.quantity || 1,
                },
              ],
            })
          }
        } catch {
          // Silently fail
        }
      },

      /**
       * Track view-product ecommerce event
       */
      trackViewProduct(product: {
        id: string
        name: string
        price: number
        brand?: string
        category?: string
        variant?: string
      }) {
        try {
          // Yandex.Metrica
          if (YANDEX_METRIKA_ID && win.ym) {
            win.ym(YANDEX_METRIKA_ID, 'reachGoal', 'view_product', {
              ecommerce: {
                detail: {
                  products: [
                    {
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      brand: product.brand,
                      category: product.category,
                      variant: product.variant,
                    },
                  ],
                },
              },
            })
          }

          // GA4
          if (GA_ID && win.gtag) {
            win.gtag('event', 'view_item', {
              currency: 'RUB',
              value: product.price,
              items: [
                {
                  item_id: product.id,
                  item_name: product.name,
                  item_brand: product.brand,
                  item_category: product.category,
                  item_variant: product.variant,
                  price: product.price,
                },
              ],
            })
          }
        } catch {
          // Silently fail
        }
      },

      /**
       * Track purchase ecommerce event
       */
      trackPurchase(order: {
        id: string
        total: number
        items: Array<{
          id: string
          name: string
          price: number
          quantity: number
          brand?: string
          category?: string
          variant?: string
        }>
      }) {
        try {
          // Yandex.Metrica
          if (YANDEX_METRIKA_ID && win.ym) {
            win.ym(YANDEX_METRIKA_ID, 'reachGoal', 'purchase', {
              order_id: order.id,
              order_price: order.total,
              currency: 'RUB',
              ecommerce: {
                purchase: {
                  actionField: {
                    id: order.id,
                    revenue: order.total,
                  },
                  products: order.items.map((item) => ({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    brand: item.brand,
                    category: item.category,
                    variant: item.variant,
                  })),
                },
              },
            })
          }

          // GA4
          if (GA_ID && win.gtag) {
            win.gtag('event', 'purchase', {
              transaction_id: order.id,
              value: order.total,
              currency: 'RUB',
              items: order.items.map((item) => ({
                item_id: item.id,
                item_name: item.name,
                item_brand: item.brand,
                item_category: item.category,
                item_variant: item.variant,
                price: item.price,
                quantity: item.quantity,
              })),
            })
          }
        } catch {
          // Silently fail
        }
      },
    }
  }
}

/**
 * Analytics component — loads Yandex.Metrica and Google Analytics scripts.
 * Must be added to layout.tsx as a client component.
 */
export function Analytics() {
  usePageView()

  useEffect(() => {
    initEcommerceHelpers()
  }, [])

  return (
    <>
      {/* Yandex.Metrica */}
      {YANDEX_METRIKA_ID && (
        <>
          <Script id="yandex-metrica" strategy="afterInteractive">
            {`
              (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
              m[i].l=1*new Date();
              for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
              k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
              (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

              ym(${YANDEX_METRIKA_ID}, "init", {
                clickmap:true,
                trackLinks:true,
                accurateTrackBounce:true,
                webvisor:true,
                ecommerce:"dataLayer"
              });
            `}
          </Script>
          {/* Noscript fallback for Yandex.Metrica pixel */}
          <noscript>
            <div>
              <img
                src={`https://mc.yandex.ru/watch/${YANDEX_METRIKA_ID}`}
                style={{ position: 'absolute', left: '-9999px' }}
                alt=""
              />
            </div>
          </noscript>
        </>
      )}

      {/* Google Analytics 4 */}
      {GA_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_ID}', {
                page_path: window.location.pathname,
              });
            `}
          </Script>
        </>
      )}
    </>
  )
}
